
import { of as observableOf,  Observable } from 'rxjs';
import { catchError, map, share, switchMap, withLatestFrom} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { AppState } from '../../interfaces/app-state.interface';
import { Store } from '@ngrx/store';
import { HttpService } from '../../services/http/http.service';
import { HttpHeaders } from '@angular/common/http';
import { UsersState } from '../../state-management/users/users.reducer';
import { SettingsService } from '../../services/settings/settings.service';
import { TraderAdvisorAction } from './trader-advisor.action';

@Injectable()
export class TraderAdvisorEffects {

    /**
     * Busca a taxa de acerto
     */
    @Effect()
    public getAccuracy$: Observable<TraderAdvisorAction> = this.actions$
        .pipe(
            ofType(TraderAdvisorAction.getAccuracy),
            withLatestFrom(this.store.select((authStore) => authStore.authentication)),
            switchMap(([action, auth]) => {
                const backendUri = this.settingService.getConfigurationKeyValue('backend');
                this.getAccuracyUri = backendUri.api + '/traderadvisor/report/acurrancyrate';
                const isLogged = !!(auth.currentUser && auth.currentUser.token);
                return this.httpService.get(this.getAccuracyUri, this.createHeaders(auth, isLogged))
                    .pipe(
                        map((result: any) => {
                            if (result) {
                                return { type: TraderAdvisorAction.getAccuracySuccess, payload: result };
                            }
                            return { type: TraderAdvisorAction.getAccuracyError, payload: result };
                        }),
                        catchError((error) => {
                            return observableOf({ type: TraderAdvisorAction.getAccuracyError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Busca a taxa de appt
     */
    @Effect()
    public getAppt$: Observable<TraderAdvisorAction> = this.actions$
        .pipe(
            ofType(TraderAdvisorAction.getAppt),
            withLatestFrom(this.store.select((authStore) => authStore.authentication)),
            switchMap(([action, auth]) => {
                const backendUri = this.settingService.getConfigurationKeyValue('backend');
                this.getApptUri = backendUri.api + '/traderadvisor/report/appt';
                const isLogged = !!(auth.currentUser && auth.currentUser.token);
                return this.httpService.get(this.getApptUri, this.createHeaders(auth, isLogged))
                    .pipe(
                        map((result: any) => {
                            if (result) {
                                return { type: TraderAdvisorAction.getApptSuccess, payload: result };
                            }
                            return { type: TraderAdvisorAction.getApptError, payload: result };
                        }),
                        catchError((error) => {
                            return observableOf({ type: TraderAdvisorAction.getApptError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Busca resultado da disciplina diária das últimas operações
     */
    @Effect()
    public getDailydiscipline$: Observable<TraderAdvisorAction> = this.actions$
        .pipe(
            ofType(TraderAdvisorAction.getDailydiscipline),
            withLatestFrom(this.store.select((authStore) => authStore.authentication)),
            switchMap(([action, auth]) => {
                const backendUri = this.settingService.getConfigurationKeyValue('backend');
                this.getDailydisciplineUri = backendUri.api + '/traderadvisor/report/dailydiscipline';
                const isLogged = !!(auth.currentUser && auth.currentUser.token);
                return this.httpService.get(this.getDailydisciplineUri, this.createHeaders(auth, isLogged))
                    .pipe(
                        map((result: any) => {
                            if (result) {
                                return { type: TraderAdvisorAction.getDailydisciplineSuccess, payload: result };
                            }
                            return { type: TraderAdvisorAction.getDailydisciplineError, payload: result };
                        }),
                        catchError((error) => {
                            return observableOf({ type: TraderAdvisorAction.getDailydisciplineError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Busca operações com perdas acima da média
     */
    @Effect()
    public getLossesGreaterThanAverage$: Observable<TraderAdvisorAction> = this.actions$
        .pipe(
            ofType(TraderAdvisorAction.getLossesGreaterThanAverage),
            withLatestFrom(this.store.select((authStore) => authStore.authentication)),
            switchMap(([action, auth]) => {
                const backendUri = this.settingService.getConfigurationKeyValue('backend');
                this.getLossesGreaterThanAverageUri = backendUri.api + '/traderadvisor/report/lossesGreaterThanAverage';
                const isLogged = !!(auth.currentUser && auth.currentUser.token);
                return this.httpService.get(this.getLossesGreaterThanAverageUri, this.createHeaders(auth, isLogged))
                    .pipe(
                        map((result: any) => {
                            if (result) {
                                return { type: TraderAdvisorAction.getLossesGreaterThanAverageSuccess, payload: result };
                            }
                            return { type: TraderAdvisorAction.getLossesGreaterThanAverageError, payload: result };
                        }),
                        catchError((error) => {
                            return observableOf({ type: TraderAdvisorAction.getLossesGreaterThanAverageError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Busca status da inscrição no whatsapp trader advisor
     */
    @Effect()
    public getWhatsappStatus$: Observable<TraderAdvisorAction> = this.actions$
        .pipe(
            ofType(TraderAdvisorAction.getWhatsappStatus),
            withLatestFrom(this.store.select((authStore) => authStore.authentication)),
            switchMap(([action, auth]) => {
                const backendUri = this.settingService.getConfigurationKeyValue('backend');
                this.getWhatsappStatusUri = backendUri.api + '/traderadvisor/participant/whatsapp-message-status';
                const isLogged = !!(auth.currentUser && auth.currentUser.token);
                return this.httpService.get(this.getWhatsappStatusUri, this.createHeaders(auth, isLogged))
                    .pipe(
                        map((result: any) => {
                            if (result) {
                                return { type: TraderAdvisorAction.getWhatsappStatusSuccess, payload: result };
                            }
                            return { type: TraderAdvisorAction.getWhatsappStatusError, payload: result };
                        }),
                        catchError((error) => {
                            return observableOf({ type: TraderAdvisorAction.getWhatsappStatusError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    /**
     * Atualiza status da inscrição no whatsapp trader advisor
     */
    @Effect()
    public setWhatsappStatus$: Observable<TraderAdvisorAction> = this.actions$
        .pipe(
            ofType(TraderAdvisorAction.setWhatsappStatus),
            withLatestFrom(this.store.select((authStore) => authStore.authentication)),
            switchMap(([action, auth]) => {
                const backendUri = this.settingService.getConfigurationKeyValue('backend');
                this.getWhatsappStatusUri = backendUri.api + '/traderadvisor/participant/whatsapp-message-status';
                const isLogged = !!(auth.currentUser && auth.currentUser.token);
                return this.httpService.put<any>(this.getWhatsappStatusUri, this.createHeaders(auth, isLogged))
                    .pipe(
                        map((result: any) => {
                            if (result) {
                                return { type: TraderAdvisorAction.setWhatsappStatusSuccess, payload: result };
                            }
                            return { type: TraderAdvisorAction.setWhatsappStatusError, payload: result };
                        }),
                        catchError((error) => {
                            return observableOf({ type: TraderAdvisorAction.setWhatsappStatusError, payload: error });
                        }),
                    );
            }),
            share(),
        );

    private getAccuracyUri: string;
    private getApptUri: string;
    private getDailydisciplineUri: string;
    private getLossesGreaterThanAverageUri: string;
    private getWhatsappStatusUri: string;

    constructor(
        private actions$: Actions<TraderAdvisorAction>,
        private store: Store<AppState>,
        private httpService: HttpService,
        private settingService: SettingsService,
    ) {}

    private createHeaders(userData: UsersState, logged: boolean = false) {
        if (!logged) {
            return new HttpHeaders({
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json; charset=UTF-8',
                'X-SessionID': `${userData.sessionID}`,
            });
        } else {
            return new HttpHeaders({
                'Content-Type': 'application/json; charset=UTF-8',
                'Authorization': `Bearer ${userData.currentUser.token}`,
            });
        }
    }
}
